import React, {SyntheticEvent, useState} from "react";
import {useHistory, useParams} from "react-router-dom";

// Logic.
import {connect, ConnectedProps, useDispatch} from "react-redux";

// Design.
import {
    AlertTypes,
    apiClient,
    BaseCrudClient,
    enumNamesToSelect,
    GenericFormContainer,
    Grid,
    IAlertData,
    Leap4Breadcrumbs,
    Mode,
    MuiIcons,
    setAlert,
    Spinner,
    Button,
    FormControl,
    IconButton,
    TextField,
    Typography,
    Toc as TocIcon,
    Create as CreateIcon,
    GpsFixed as GpsFixedIcon
} from "@leap4/frontend";
import { styles } from "./style";
import {baseBackendRoute, priceRulesBaseUrl} from "../../../../config/generals";
import {IPriceRulePopulatedAmount, PriceRuleParamBy, PriceRulesTypes} from "../PriceRules.interface";
import {PriceRuleMatchingTagsTable} from "./PriceRuleMatchingTagsTable";
import {RootState} from "../../../../interfaces/root-state.interface";
import {useQuery} from "react-query";
import {Server} from "@leap4/auth";
import {OneOrgOnly} from "../../../shared/OneOrgOnly/OneOrgOnly";

const {Restore: RestoreIcon, AutoFixNormal: AutoFixNormalIcon} = MuiIcons;

const {add, update, deleteInstance, getById} = BaseCrudClient;

interface IFormData extends Partial<IPriceRulePopulatedAmount> {
}

const mapStateToProps = (state: RootState) => ({});

const mapActions = {
    setAlert
}


const connector = connect(mapStateToProps, mapActions);

type PropsFromRedux = ConnectedProps<typeof connector>;

export interface IReduxAlertProps extends PropsFromRedux {
    setAlert: (alert: IAlertData) => void
}


const _AddEditPriceRule = (props: IReduxAlertProps): JSX.Element => {
    const {setAlert} = props;

    const RulesNameToSelect = enumNamesToSelect(PriceRulesTypes, undefined, undefined, "Price Rule Type");
    const RuleParamsByToSelect = enumNamesToSelect(PriceRuleParamBy, undefined, undefined, "Rule By")
    const history = useHistory();
    const params = useParams<{ id?: string }>();
    const dispatch = useDispatch();

    const schemaName = "priceRule";
    const addInstanceRoute = `/${schemaName}s/add`;
    const editInstanceRoute = `/${schemaName}s/`;

    const [isAddPage, setIsAddPage] = useState<boolean>(true);
    const [linkedTags, setLinkedTags] = useState([])
    const [testTags, setTestTags] = useState([])
    const [initialRegex, setInitialRegex] = useState('')


    const [priceRule, setPriceRule] = useState<IFormData>({
        name: '',
        rule: PriceRulesTypes["Revenue Share"],
        params: {number: 0, by: PriceRuleParamBy.PERCENT}
    });


    const handleFormChange = (e: any) => {
        let {name, value} = e.target;
        if (name === 'paramNum') {
            return setPriceRule({...priceRule, params: {number: Number(value), by: priceRule.params.by}})
        }
        if (name === 'paramBy') {
            return setPriceRule({...priceRule, params: {number: priceRule.params.number, by: value}})
        }
        setPriceRule({...priceRule, [name]: value});
    }


    const handleSubmit = async (ev: SyntheticEvent) => {
        ev?.preventDefault();
        if (!priceRule.name) return setAlert({msg: 'Please Enter a Name For Price Rule', type: AlertTypes.WARNING})
        if (!priceRule.regex) return setAlert({msg: 'Please Enter a Regex For Price Rule', type: AlertTypes.WARNING})
        if (priceRule.rule === PriceRulesTypes["Revenue Share"]) {
            if (priceRule.params.number > 100 || priceRule.params.number < 1)
                return setAlert({msg: 'Revenue Share % must be between 1 to 100', type: AlertTypes.WARNING})
        } else if (priceRule.rule === PriceRulesTypes["Buffer"]) {
            if (priceRule.params.by === PriceRuleParamBy.PERCENT) {
                if (priceRule.params.number < 100 || priceRule.params.number > 200)
                    return setAlert({msg: 'Buffer % must be above 100', type: AlertTypes.WARNING})
            }
        }
        await refetchSubmit()
    };

    const savePriceRule = async () => {
        if (!priceRule.name || !priceRule.regex || !priceRule.rule) {
            return;
        }
        try {
            if (isAddPage) {
                await add<any>(priceRule, schemaName, history, dispatch, baseBackendRoute);
            } else if (params.id && params.id != "add") {
                await update<any>(params.id, priceRule, schemaName, history, dispatch, baseBackendRoute);
            }
            setAlert({msg: "Saved Successfully", type: AlertTypes.SUCCESS})
        } catch (err) {
            console.log(err)
        }
    }

    const handleResetChanges = () => {
        setTestTags([])
        setPriceRule({...priceRule, regex: initialRegex})
    }

    const fetchTestTags = async () => {
        if (!priceRule.regex) return setAlert({msg: "Can not test empty text", type: AlertTypes.WARNING})
        const apiResponse = await apiClient.post(`${priceRulesBaseUrl}/test`, {
            regex: priceRule.regex
        })
        if (apiResponse.data.length) setTestTags(apiResponse.data)
        else {
            setTestTags(null)
            setAlert({msg: `No Tags matching the regex: ${priceRule.regex}`, type: AlertTypes.WARNING})
        }
    }

    const fetchInitialData = async () => {
        const id = params.id;
        if (id && id !== "add") {
            setIsAddPage(false);
            const instanceData = await getById(id, schemaName, dispatch, baseBackendRoute);
            setPriceRule({...instanceData.priceRule, regex: instanceData.priceRule.regex.toString()});
            setLinkedTags(instanceData.tagsTable)
            setInitialRegex(instanceData.priceRule.regex.toString())
        } else {
            setPriceRule({
                name: "",
                rule: PriceRulesTypes["Revenue Share"],
                params: {number: 0, by: PriceRuleParamBy.PERCENT}
            })
        }
    }

    const deleteFunc = async () => {
        await deleteInstance(String(params.id), schemaName, history, dispatch, baseBackendRoute)
    }


    const {isLoading: isLoadingSubmit, refetch: refetchSubmit} = useQuery(
        'save-price-rule-query',
        savePriceRule,
        {
            refetchOnWindowFocus: false,
            cacheTime: 0,
            enabled: false,
            onError: (err: any) => setAlert({msg: err.message, type: AlertTypes.FAILURE})
        });

    const {isLoading: isLoadingDelete, refetch: refetchDelete} = useQuery(
        'delete-price-rule-query',
        deleteFunc,
        {
            refetchOnWindowFocus: false,
            cacheTime: 0,
            enabled: false,
            onError: (err: any) => setAlert({msg: err.message, type: AlertTypes.FAILURE})
        });

    const {isLoading: isLoadingTestTags, refetch: refetchTestTags} = useQuery(
        'fetch-test-tags-query',
        fetchTestTags,
        {
            // onSuccess: (ev: AxiosResponse) => handlePredictiveData(ev.data),
            refetchOnWindowFocus: false,
            cacheTime: 0,
            enabled: false,
            onError: (err: any) => setAlert({msg: err.message, type: AlertTypes.FAILURE})
        });

    const {isLoading: isLoadingInitialData} = useQuery(
        'fetch-price-rule-data-query',
        fetchInitialData,
        {
            refetchOnWindowFocus: false,
            cacheTime: 0,
            onError: (err: any) => setAlert({msg: err.message, type: AlertTypes.FAILURE})
        });

    const BufferFields = () => {
        return (
            <Grid container alignItems={'flex-start'} gap={1} mt={1.5}>
                <Grid item lg={3} sm={12} width={"20%"} mt={1.8}>
                    <FormControl variant="outlined" style={styles.formControl}>
                        <RuleParamsByToSelect
                            required
                            fullWidth
                            value={priceRule.params.by}
                            onChange={handleFormChange}
                            name="paramBy"
                        />
                    </FormControl>
                </Grid>
                <Grid item width={"20%"}>
                    <TextField
                        variant="outlined"
                        margin="normal"
                        required
                        label={PriceRuleParamBy.PERCENT === priceRule.params.by ? "Buffer Percent (100+)" : "Buffer Fixed Price (RPM)"}
                        name="paramNum"
                        onChange={handleFormChange}
                        value={priceRule?.params?.number}
                        type={'number'}
                    />
                </Grid>
            </Grid>
        )
    }


    const isLoading = isLoadingTestTags || isLoadingInitialData || isLoadingDelete
    const lastChosenOrganization = localStorage.getItem(Server.headers.X_ORGANZIZATION).split('-')
    if (lastChosenOrganization.length > 1) {
        return <OneOrgOnly />
    }
    return (
        <>
            <Leap4Breadcrumbs breadcrumbs={[
                {
                    text: `${schemaName[0].toUpperCase() + schemaName.slice(1)}s Dashboard`,
                    link: "/pricerules",
                    icon: <TocIcon/>
                },
                {
                    text: isAddPage ? "Create" : "Update",
                    link: isAddPage ? addInstanceRoute : editInstanceRoute,
                    icon: isAddPage ? <CreateIcon/> : <AutoFixNormalIcon/>
                },
                {
                    text: isAddPage ? !priceRule?.name ? "New" : priceRule.name :
                        !priceRule?.name ? "Loading" : priceRule?.name[0]?.toUpperCase() + priceRule?.name?.slice(1),
                    link: "",
                    icon: <GpsFixedIcon/>
                }
            ]}/>
            <GenericFormContainer mode={isAddPage ? Mode.Create : Mode.Edit} onCreateOrEditFunc={handleSubmit}
                                  disabled={isLoading}
                                  style={{height: '450px', padding: '20px', margin: "35px 25px", borderRadius: "16px",}}
                                  onDeleteFunction={refetchDelete}>


                {isLoading ? <Spinner/> :
                    <form style={styles.form} noValidate onSubmit={handleSubmit}>
                        <Grid container direction={"column"} alignItems={"flex-start"} height={'auto'}>
                            <Grid container justifyContent={"space-between"} spacing={{lg: 3, xs: 1}}>
                                <Grid item>
                                    <Typography variant="h5">
                                        Price Rule Info
                                    </Typography>
                                </Grid>
                            </Grid>

                            <Grid item lg={6} width={"60%"}>
                            </Grid>

                            <Grid item lg={6} sm={12} width={"60%"}>
                                <TextField
                                    variant="outlined"
                                    margin="normal"
                                    required
                                    fullWidth
                                    label="Name"
                                    name="name"
                                    autoFocus
                                    autoComplete="name"
                                    onChange={handleFormChange}
                                    value={priceRule?.name}
                                />
                            </Grid>
                            <Grid container justifyContent={'flex-start'} gap={1} alignItems={'center'}>
                                <Grid item lg={6} sm={12} width={"60%"}>
                                    <TextField
                                        variant="outlined"
                                        margin="normal"
                                        required
                                        fullWidth
                                        label="Regex Expression"
                                        name="regex"
                                        autoComplete="regex"
                                        onChange={handleFormChange}
                                        value={priceRule?.regex}
                                    />
                                </Grid>
                                <Grid item>
                                    <Button variant={'contained'} onClick={() => refetchTestTags()}
                                            disabled={priceRule.regex == initialRegex}>
                                        TEST
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <IconButton onClick={handleResetChanges} color={'primary'}>
                                        <RestoreIcon/>
                                    </IconButton>
                                </Grid>
                            </Grid>
                            <Grid item lg={6} sm={12} width={"30%"} mt={1.5}>
                                <FormControl variant="outlined" style={styles.formControl}>
                                    <RulesNameToSelect
                                        required
                                        fullWidth
                                        value={priceRule.rule}
                                        onChange={handleFormChange}
                                        name="rule"
                                    />
                                </FormControl>
                            </Grid>
                            {
                                priceRule.rule === PriceRulesTypes["Revenue Share"] ? (
                                    <Grid item lg={6} sm={12} width={"20%"}>
                                        <TextField
                                            variant="outlined"
                                            margin="normal"
                                            required
                                            fullWidth
                                            label="Revenue Share Percent"
                                            name="paramNum"
                                            onChange={handleFormChange}
                                            value={priceRule?.params?.number}
                                            type={'number'}
                                        />
                                    </Grid>
                                ) : priceRule.rule === PriceRulesTypes["Buffer"] ? (
                                    <BufferFields />
                                ) : null
                            }
                        </Grid>
                    </form>}
            </GenericFormContainer>
            <PriceRuleMatchingTagsTable linkedTags={testTags?.length ? testTags : linkedTags} isLoading={isLoading}
                                        isEmptyTable={!testTags}/>
        </>
    );
}


export const AddEditPriceRule = connector(_AddEditPriceRule);
